import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Calculator, Check, Plus, Trash2, AlertTriangle, X } from "lucide-react";
import api, { formatApiError, money } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

const SOURCE_COLORS = {
  "Compra local": "bg-blue-100 text-blue-700",
  "Importación": "bg-purple-100 text-purple-700",
  "Servicio": "bg-teal-100 text-teal-700",
  "Inventario": "bg-amber-100 text-amber-700",
};

const n = (v) => { const x = Number(v); return isNaN(x) ? 0 : x; };

export default function InvoiceCosting({ invoice, onClose, onSaved, endpoint, title }) {
  const cur = invoice.currency;
  const [items, setItems] = useState(() => invoice.items.map((it) => ({
    ...it,
    cost_sources: (it.cost_sources || []).map((s) => ({ ...s, qty: n(s.qty), unit_cost: n(s.unit_cost) })),
    manual_cost: (it.cost_sources && it.cost_sources.length) ? 0 : n(it.cost_unit || 0),
  })));
  const [extras, setExtras] = useState(() => (invoice.extra_costs || []).map((e) => ({
    concept: e.concept || "", amount: n(e.amount), scope: e.scope || "invoice",
    item_index: e.item_index ?? null,
  })));
  const [saving, setSaving] = useState(false);
  const [searchIdx, setSearchIdx] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ groups: [], products: [] });
  const [loading, setLoading] = useState(false);
  const [searchCur, setSearchCur] = useState("GTQ");

  useEffect(() => {
    if (searchIdx === null) return;
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/costing/search?q=${encodeURIComponent(q)}`)
        .then((r) => setResults(r.data || { groups: [], products: [] }))
        .catch(() => setResults({ groups: [], products: [] }))
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [q, searchIdx]);

  // ---- Cálculos por ítem ----
  const allocQty = (it) => (it.cost_sources || []).reduce((s, x) => s + n(x.qty), 0);
  const baseCost = (it) => (it.cost_sources || []).length
    ? (it.cost_sources).reduce((s, x) => s + n(x.qty) * n(x.unit_cost), 0)
    : n(it.manual_cost) * n(it.qty);
  const weightedUnit = (it) => {
    if ((it.cost_sources || []).length) { const q2 = allocQty(it); return q2 ? baseCost(it) / q2 : 0; }
    return n(it.manual_cost);
  };
  const itemExtraPU = (idx) => extras.filter((e) => e.scope === "item" && e.item_index === idx).reduce((s, e) => s + n(e.amount), 0);
  const itemExtraTotal = (it, idx) => itemExtraPU(idx) * n(it.qty);
  const costLine = (it, idx) => baseCost(it) + itemExtraTotal(it, idx);

  const openSearch = (i) => { setSearchIdx(i); setQ(items[i].description || items[i].code || ""); setResults({ groups: [], products: [] }); };

  const addSource = (src) => {
    setItems((prev) => prev.map((it, idx) => {
      if (idx !== searchIdx) return it;
      const remaining = Math.max(0, n(it.qty) - allocQty(it));
      const qty = remaining || n(it.qty) || 1;
      return { ...it, cost_sources: [...(it.cost_sources || []), { ...src, qty }] };
    }));
    toast.success("Fuente agregada al ítem");
  };
  const setSourceQty = (i, si, val) => setItems((prev) => prev.map((it, idx) => idx === i
    ? { ...it, cost_sources: it.cost_sources.map((s, j) => j === si ? { ...s, qty: val } : s) } : it));
  const removeSource = (i, si) => setItems((prev) => prev.map((it, idx) => idx === i
    ? { ...it, cost_sources: it.cost_sources.filter((_, j) => j !== si) } : it));
  const setManual = (i, val) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, manual_cost: val } : it));

  const addExtra = () => setExtras((p) => [...p, { concept: "", amount: "", scope: "invoice", item_index: null }]);
  const setExtra = (i, k, v) => setExtras((p) => p.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  const removeExtra = (i) => setExtras((p) => p.filter((_, idx) => idx !== i));

  const totals = useMemo(() => {
    const itemsCost = items.reduce((s, it, idx) => s + costLine(it, idx), 0);
    const invoiceExtras = extras.filter((e) => e.scope === "invoice").reduce((s, e) => s + n(e.amount), 0);
    const costTotal = itemsCost + invoiceExtras;
    const subtotal = n(invoice.subtotal);
    const profit = subtotal - costTotal;
    const margin = subtotal ? (profit / subtotal) * 100 : 0;
    return { itemsCost, invoiceExtras, costTotal, subtotal, profit, margin };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, extras, invoice.subtotal]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        items: items.map((it, index) => ({
          index,
          sources: (it.cost_sources || []).map((s) => ({
            doc_id: s.doc_id || "", source_type: s.source_type || "", ref: s.ref || "",
            supplier: s.supplier || "", code: s.code || "", description: s.description || "",
            unit_cost: n(s.unit_cost), qty: n(s.qty), currency: s.currency || cur,
          })),
          cost_unit: (it.cost_sources || []).length ? 0 : n(it.manual_cost),
          source: it.cost_source || "",
        })),
        extras: extras.filter((e) => e.concept || n(e.amount)).map((e) => ({
          concept: e.concept, amount: n(e.amount), scope: e.scope,
          item_index: e.scope === "item" ? e.item_index : null,
        })),
      };
      await api.post(endpoint || `/invoices/${invoice.id}/costing`, payload);
      toast.success("Costeo guardado");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="rounded-sm max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="costing-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Calculator className="h-5 w-5 text-primary" /> {title || `Costear factura ${invoice.number}`}</DialogTitle>
            <DialogDescription>{invoice.client_name} · Asigna el costo de cada ítem tomando unidades de una o varias facturas de compra. Puedes agregar gastos por ítem (por unidad) o para toda la factura.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3" data-testid="costing-items">
            {items.map((it, i) => {
              const alloc = allocQty(it);
              const hasSources = (it.cost_sources || []).length > 0;
              const mismatch = hasSources && Math.abs(alloc - n(it.qty)) > 0.001;
              const wu = weightedUnit(it);
              const line = costLine(it, i);
              const profitLine = n(it.line_total) - line;
              return (
                <div key={i} className="border border-border rounded-sm p-3 space-y-2" data-testid={`costing-row-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{it.description}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">{it.code || "—"} · Vende {n(it.qty)} u a {money(cur, it.unit_price)} c/u</div>
                    </div>
                    <Button variant="outline" size="sm" className="rounded-sm h-8 gap-1 text-xs shrink-0" onClick={() => openSearch(i)} data-testid={`costing-search-btn-${i}`}><Search className="h-3.5 w-3.5" /> Buscar costo</Button>
                  </div>

                  {/* Fuentes del ítem */}
                  {hasSources && (
                    <div className="border border-border/70 rounded-sm overflow-hidden">
                      <table className="w-full text-xs">
                        <thead><tr className="bg-muted text-left text-[10px] uppercase text-muted-foreground">
                          <th className="px-2 py-1.5">Fuente / Factura</th><th className="px-2 py-1.5 text-right">Costo unit.</th>
                          <th className="px-2 py-1.5 text-right w-24">Unidades</th><th className="px-2 py-1.5 text-right">Costo</th><th className="w-8"></th></tr></thead>
                        <tbody>
                          {it.cost_sources.map((s, si) => (
                            <tr key={si} className="border-t border-border" data-testid={`costing-src-${i}-${si}`}>
                              <td className="px-2 py-1.5">
                                <span className={`text-[9px] font-semibold px-1 py-0.5 rounded-sm ${SOURCE_COLORS[s.source_type] || "bg-muted"}`}>{s.source_type}</span>
                                <span className="ml-1 font-mono">{s.ref}</span>
                                <div className="text-muted-foreground truncate max-w-[16rem]">{s.supplier}</div>
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono">{money(s.currency || cur, s.unit_cost)}</td>
                              <td className="px-2 py-1.5 text-right">
                                <Input type="text" inputMode="decimal" value={s.qty}
                                  onChange={(e) => { const v = e.target.value; if (v === "" || /^\d*\.?\d*$/.test(v)) setSourceQty(i, si, v); }}
                                  className="rounded-sm h-7 w-20 text-right font-mono ml-auto" data-testid={`costing-src-qty-${i}-${si}`} />
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono">{money(cur, n(s.qty) * n(s.unit_cost))}</td>
                              <td className="px-1"><button type="button" onClick={() => removeSource(i, si)} className="text-destructive p-1" data-testid={`costing-src-rm-${i}-${si}`}><X className="h-3.5 w-3.5" /></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {!hasSources && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">Costo unitario manual:</span>
                      <Input type="text" inputMode="decimal" value={it.manual_cost}
                        onChange={(e) => { const v = e.target.value; if (v === "" || /^\d*\.?\d*$/.test(v)) setManual(i, v); }}
                        className="rounded-sm h-8 w-28 text-right font-mono" data-testid={`costing-cost-${i}`} />
                      <span className="text-muted-foreground">o usa "Buscar costo" para tomar de facturas.</span>
                    </div>
                  )}

                  {mismatch && (
                    <div className="flex items-center gap-1.5 text-[11px] text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-sm px-2 py-1" data-testid={`costing-mismatch-${i}`}>
                      <AlertTriangle className="h-3.5 w-3.5" /> Asignaste {alloc} u de {n(it.qty)} vendidas. Ajusta las cantidades para que cuadren.
                    </div>
                  )}

                  <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 text-xs pt-1 border-t border-border/60">
                    <span className="text-muted-foreground">Costo unit. (ponderado): <b className="font-mono text-foreground">{money(cur, wu)}</b></span>
                    {itemExtraPU(i) > 0 && <span className="text-amber-600">Gasto ítem: {money(cur, itemExtraPU(i))}/u</span>}
                    <span className="text-muted-foreground">Costo total: <b className="font-mono text-foreground" data-testid={`costing-line-${i}`}>{money(cur, line)}</b></span>
                    <span className={profitLine < 0 ? "text-red-600 font-semibold" : "text-green-700 font-semibold"}>Utilidad: {money(cur, profitLine)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Gastos / costos extra */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Gastos / costos adicionales</p>
              <Button variant="outline" size="sm" className="rounded-sm h-8 gap-1 text-xs" onClick={addExtra} data-testid="costing-add-extra"><Plus className="h-3.5 w-3.5" /> Agregar gasto</Button>
            </div>
            {extras.length === 0 ? <p className="text-xs text-muted-foreground">Sin gastos adicionales.</p> : (
              <div className="space-y-2">
                {extras.map((e, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`costing-extra-${i}`}>
                    <Input value={e.concept} onChange={(ev) => setExtra(i, "concept", ev.target.value)} placeholder="Concepto (flete, mensajería...)" className="rounded-sm h-9 flex-1 min-w-[10rem]" data-testid={`costing-extra-concept-${i}`} />
                    <Select value={e.scope === "item" ? `item:${e.item_index ?? 0}` : "invoice"}
                      onValueChange={(v) => { if (v === "invoice") { setExtra(i, "scope", "invoice"); setExtra(i, "item_index", null); } else { setExtra(i, "scope", "item"); setExtra(i, "item_index", Number(v.split(":")[1])); } }}>
                      <SelectTrigger className="rounded-sm h-9 w-52 text-xs" data-testid={`costing-extra-scope-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="invoice">Toda la factura (total)</SelectItem>
                        {items.map((it, idx) => <SelectItem key={idx} value={`item:${idx}`}>Ítem: {(it.description || it.code || `#${idx + 1}`).slice(0, 30)} (x unidad)</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input type="text" inputMode="decimal" value={e.amount}
                      onChange={(ev) => { const v = ev.target.value; if (v === "" || /^\d*\.?\d*$/.test(v)) setExtra(i, "amount", v); }}
                      placeholder={e.scope === "item" ? "Monto/unidad" : "Monto"} className="rounded-sm h-9 w-32 text-right font-mono" data-testid={`costing-extra-amount-${i}`} />
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive shrink-0" onClick={() => removeExtra(i)} data-testid={`costing-extra-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <div className="w-72 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal venta</span><span className="font-mono">{money(cur, totals.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Costo ítems (con gastos por ítem)</span><span className="font-mono">{money(cur, totals.itemsCost)}</span></div>
              {totals.invoiceExtras > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Gastos de factura</span><span className="font-mono text-amber-600">{money(cur, totals.invoiceExtras)}</span></div>}
              <div className="flex justify-between border-t border-border pt-1"><span className="text-muted-foreground">Costo total</span><span className="font-mono font-semibold">{money(cur, totals.costTotal)}</span></div>
              <div className={`flex justify-between font-bold ${totals.profit < 0 ? "text-red-600" : "text-green-700"}`}><span>UTILIDAD</span><span className="font-mono" data-testid="costing-profit">{money(cur, totals.profit)}</span></div>
              <div className="flex justify-between text-xs"><span className="text-muted-foreground">Margen</span><span className={`font-mono font-bold ${totals.margin < 0 ? "text-red-600" : "text-green-700"}`} data-testid="costing-margin">{totals.margin.toFixed(1)}%</span></div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancelar</Button>
            <Button className="rounded-sm font-bold gap-2" onClick={save} disabled={saving} data-testid="costing-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Guardar costeo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buscador agrupado por factura */}
      <Dialog open={searchIdx !== null} onOpenChange={(o) => !o && setSearchIdx(null)}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="costing-search-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Buscar costo por factura de compra</DialogTitle>
            <DialogDescription>{searchIdx !== null && items[searchIdx]?.description} · Escribe proveedor, N° de factura o nombre del producto. Se muestran las facturas completas para confirmar cuál es.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Proveedor, factura, código o nombre de producto..." className="pl-9 rounded-sm" data-testid="costing-search-input" />
          </div>
          <div className="flex items-center gap-1 mt-2">
            <span className="text-[11px] text-muted-foreground mr-1">Ver costos en:</span>
            <Button size="sm" variant={searchCur === "GTQ" ? "default" : "outline"} className="rounded-sm h-7 text-xs" onClick={() => setSearchCur("GTQ")} data-testid="costing-cur-gtq">Quetzales</Button>
            <Button size="sm" variant={searchCur === "USD" ? "default" : "outline"} className="rounded-sm h-7 text-xs" onClick={() => setSearchCur("USD")} data-testid="costing-cur-usd">Dólares</Button>
          </div>
          <div className="flex-1 overflow-y-auto mt-2 min-h-[18rem] space-y-3">
            {loading ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> :
              (results.groups.length === 0 && results.products.length === 0) ? <p className="text-center text-sm text-muted-foreground py-10">Sin resultados.</p> : (
                <>
                  {results.groups.map((g, gi) => (
                    <div key={gi} className="border border-border rounded-sm overflow-hidden" data-testid={`costing-group-${gi}`}>
                      <div className="flex items-center justify-between gap-2 bg-muted px-3 py-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${SOURCE_COLORS[g.source] || "bg-white"}`}>{g.source}</span>
                            <span className="font-mono text-xs font-bold">{g.number}</span>
                            {g.doc_number ? <span className="text-[11px] text-muted-foreground">Doc: {g.doc_number}</span> : null}
                          </div>
                          <div className="text-[11px] text-muted-foreground truncate">{g.supplier} · {g.date || "s/f"} · Total {searchCur === "GTQ" ? money("GTQ", g.total_gtq ?? g.total) : money(g.currency, g.total)}{g.currency === "USD" ? ` · TC ${g.exchange_rate}` : ""}</div>
                        </div>
                      </div>
                      <table className="w-full text-xs">
                        <thead><tr className="text-left text-[10px] uppercase text-muted-foreground border-b border-border">
                          <th className="px-2 py-1">Producto</th><th className="px-2 py-1 text-right">Comprado</th><th className="px-2 py-1 text-right">Costo unit.</th><th className="w-16"></th></tr></thead>
                        <tbody>
                          {g.items.map((it, ii) => (
                            <tr key={ii} className="border-t border-border/60 hover:bg-muted/40" data-testid={`costing-group-${gi}-item-${ii}`}>
                              <td className="px-2 py-1.5"><span className="font-mono text-[10px] text-muted-foreground">{it.code}</span><br />{it.description}</td>
                              <td className="px-2 py-1.5 text-right font-mono">{it.qty}</td>
                              <td className="px-2 py-1.5 text-right font-mono font-bold">{searchCur === "GTQ" ? money("GTQ", it.unit_cost_gtq ?? it.unit_cost) : money(g.currency, it.unit_cost)}</td>
                              <td className="px-2 py-1.5 text-right"><Button size="sm" className="rounded-sm h-7 text-xs gap-1" onClick={() => addSource({ doc_id: g.doc_id, source_type: g.source, ref: g.number, supplier: g.supplier, code: it.code, description: it.description, unit_cost: (it.unit_cost_gtq ?? it.unit_cost), currency: "GTQ" })} data-testid={`costing-add-src-${gi}-${ii}`}><Plus className="h-3 w-3" /> Tomar</Button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                  {results.products.length > 0 && (
                    <div className="border border-border rounded-sm overflow-hidden" data-testid="costing-inventory-group">
                      <div className="bg-amber-100 text-amber-800 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider">Inventario (costo promedio del producto)</div>
                      <table className="w-full text-xs">
                        <tbody>
                          {results.products.map((p, pi) => (
                            <tr key={pi} className="border-t border-border/60 hover:bg-muted/40" data-testid={`costing-prod-${pi}`}>
                              <td className="px-2 py-1.5"><span className="font-mono text-[10px] text-muted-foreground">{p.code}</span><br />{p.description}</td>
                              <td className="px-2 py-1.5 text-right font-mono font-bold">{money(p.currency, p.unit_cost)}</td>
                              <td className="px-2 py-1.5 text-right"><Button size="sm" variant="outline" className="rounded-sm h-7 text-xs gap-1" onClick={() => addSource({ doc_id: "", source_type: "Inventario", ref: p.code, supplier: p.brand, code: p.code, description: p.description, unit_cost: p.unit_cost, currency: p.currency })} data-testid={`costing-add-prod-${pi}`}><Plus className="h-3 w-3" /> Tomar</Button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setSearchIdx(null)} data-testid="costing-search-done">Listo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
